import React from 'react'
import { RelativePosition } from '../../../../hooks/use-bounds-positioning'
import { Step } from 'react-joyride'
import { TriangleContainer } from '../tooltip/style'

interface TriangleProps {
  borderColour: string
  backgroundColour: string
  borderThickness: number //thickness
  pointing: RelativePosition
  step: Step
}

export const Triangle: React.FC<TriangleProps> = ({
  borderColour,
  backgroundColour,
  borderThickness,
  pointing,
  step,
}) => {
  const border = borderThickness || 0
  const colour = backgroundColour
  //arrow width and height as defined in figma [here](https://www.figma.com/file/PH3SUeDjMt6h7sR97xkyJx/App?type=design&node-id=9647-18231&t=IXn66BEZJ9YZjxsi-0)
  const x = 12
  const y = 24

  // position of arrow so it sits over the border of the tooltip
  const left = x - 2

  // centered
  const top = y / 2 - 2

  const borders: { first: React.ReactElement | null; second: React.ReactElement | null } = {
    first: null,
    second: null,
  }

  const LEFT_BORDERS_TOP = [0, y / 2, x, 0]
  const LEFT_BORDERS_BOTTOM = [0, y / 2 - 1, x, y]

  const RIGHT_BORDERS_TOP = [x * 2, y / 2, x - 1, 0]
  const RIGHT_BORDERS_BOTTOM = [x * 2, y / 2, x - 1, y]

  const TOP_BORDERS_LEFT = [x + 1, 0, 0, y / 2]
  const TOP_BORDERS_RIGHT = [x, 0, x * 2, y / 2]

  let BOTTOM_BORDERS_LEFT = [0, y / 2 + 1, x, y]
  let BOTTOM_BORDERS_RIGHT = [x, y, x * 2, y / 2 + 1]

  // position of whole arrow around container
  const LEFT_OF_CONTAINER = `-${left}px`
  const RIGHT_OF_CONTAINER = `calc(100% - ${left + border + 1}px)`
  const TOP_OF_CONTAINER = `-${top}px`

  const BOTTOM_OF_CONTAINER = `calc(90% + ${border - 1}px)`

  //position of arrow if left or right of container (vertical)
  const VERTICAL_TOP = `calc(5% + ${x}px)`
  const VERTICAL_MIDDLE = `calc(50% - ${y / 2}px)`
  const VERTICAL_BOTTOM = `90%`

  //position of arrow if top or bottom of container (horizontal)
  const HORIZONTAL_LEFT = `${x}px`
  const HORIZONTAL_MIDDLE = `calc(50% - ${x}px)`
  const HORIZONTAL_RIGHT = `calc(90% - ${x}px)`

  const styledPositions = {
    left: LEFT_OF_CONTAINER,
    top: VERTICAL_TOP,
  }

  // function to create the lines around the triangle
  const buildBorderLine = (coords: number[]) => {
    const colour = borderColour || 'black'
    return (
      <line
        x1={coords[0]}
        y1={coords[1]}
        x2={coords[2]}
        y2={coords[3]}
        stroke={colour}
        strokeWidth={3}
      ></line>
    )
  }

  /**
   * ====================
   * start: Force triangle positioning based on target
   * ====================
   */
  if (
    ['#tour-allocation', '#tour-stats'].includes(step.target as string) &&
    step.placement === 'bottom'
  ) {
    pointing = 'bottom'
  }

  // This step is only on mobile
  if (['.tour-buy-button'].includes(step.target as string)) {
    pointing = 'bottom-end'
  }

  if (['.tour-confirm-transaction'].includes(step.target as string) && step.placement === 'top') {
    pointing = 'bottom'
  }

  if (['.Toastify__toast-body'].includes(step.target as string) && step.placement === 'bottom') {
    pointing = 'top'
  }

  if (['#tour-composition'].includes(step.target as string) && step.placement === 'top') {
    BOTTOM_BORDERS_LEFT = [0, y / 2 - 1, x, y]
    BOTTOM_BORDERS_RIGHT = [x, y, x * 2, y / 2 - 1]
    pointing = 'bottom'
  }

  /**
   * ====================
   * end: Force triangle positioning based on target
   * ====================
   */

  /**
   * Programmatic positioning of triangle and its borders, based on the "pointing" const
   */
  if (pointing.startsWith('top')) {
    if (border && border > 0) {
      borders.first = buildBorderLine(TOP_BORDERS_LEFT)
      borders.second = buildBorderLine(TOP_BORDERS_RIGHT)
    }
    styledPositions.top = TOP_OF_CONTAINER

    if (pointing.includes('start')) {
      styledPositions.left = HORIZONTAL_LEFT
    } else if (pointing.includes('end')) {
      styledPositions.left = HORIZONTAL_RIGHT
    } else {
      styledPositions.left = HORIZONTAL_MIDDLE
    }
  } else if (pointing.startsWith('bottom')) {
    if (border && border > 0) {
      borders.first = buildBorderLine(BOTTOM_BORDERS_LEFT)
      borders.second = buildBorderLine(BOTTOM_BORDERS_RIGHT)
    }
    styledPositions.top = BOTTOM_OF_CONTAINER
    styledPositions.left = HORIZONTAL_MIDDLE
  } else if (pointing.startsWith('left')) {
    if (border && border > 0) {
      borders.first = buildBorderLine(LEFT_BORDERS_TOP)
      borders.second = buildBorderLine(LEFT_BORDERS_BOTTOM)
    }
    styledPositions.left = LEFT_OF_CONTAINER
    if (pointing.includes('start')) {
      styledPositions.top = VERTICAL_TOP
    } else if (pointing.includes('end')) {
      styledPositions.top = VERTICAL_BOTTOM
    } else {
      styledPositions.top = VERTICAL_MIDDLE
    }
  } else if (pointing.startsWith('right')) {
    if (border && border > 0) {
      borders.first = buildBorderLine(RIGHT_BORDERS_TOP)
      borders.second = buildBorderLine(RIGHT_BORDERS_BOTTOM)
    }
    styledPositions.left = RIGHT_OF_CONTAINER
    if (pointing.includes('start')) {
      styledPositions.top = VERTICAL_TOP
    } else if (pointing.includes('end')) {
      styledPositions.top = VERTICAL_BOTTOM
    } else {
      styledPositions.top = VERTICAL_MIDDLE
    }
  }

  // the 'triangle' is actually a diamond shape, with borders around the outer edges
  const diamondPoints = '12,0 24,12 12,24 0,12'

  return (
    <TriangleContainer left={styledPositions.left} top={styledPositions.top}>
      <svg height={24} version="1.1" width={24} xmlns="http://www.w3.org/2000/svg">
        <polygon fill={colour} points={diamondPoints} />
        {borders.first}
        {borders.second}
      </svg>
    </TriangleContainer>
  )
}

export default Triangle
